/**
 * DataTable — reusable list/admin table.
 *
 * The §10.6 admin pages share a common shape: search box, a
 * row-actions cell, simple pagination. Centralizing this here means
 * each admin page focuses on its own column projection and mutation
 * wiring; layout, RTL behavior, and pagination control come for free.
 *
 * Generic over the row type so we can express "this column projects
 * field X" without losing type safety. Columns are an array rather
 * than a map so the order is explicit.
 */

import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface Column<T> {
  key: string;
  header: string;
  /** Cell renderer. Returning React.ReactNode keeps the table flexible
   *  enough for icons, badges, action buttons inside cells. */
  render: (row: T) => React.ReactNode;
  /** Hidden on narrower viewports — the admin tables are dense and we
   *  hide truly secondary columns rather than letting the table scroll
   *  horizontally on phones. */
  hideOnMobile?: boolean;
}

interface Props<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  search?: {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
  };
  /** When provided, renders a top-right "Add" button. */
  addButton?: { label: string; onClick: () => void };
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  emptyMessage?: string;
  isLoading?: boolean;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  search,
  addButton,
  page,
  totalPages,
  onPageChange,
  emptyMessage,
  isLoading,
}: Props<T>) {
  const { t } = useTranslation();
  const empty = !isLoading && rows.length === 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {search ? (
          <input
            type="search"
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder ?? t("common.search")}
            className="flex-1 min-w-[12rem] rounded-md border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark px-3 py-2 text-sm text-fg dark:text-fg-dark"
          />
        ) : (
          <div className="flex-1" />
        )}
        {addButton ? (
          <button
            type="button"
            onClick={addButton.onClick}
            className="rounded-md bg-fg dark:bg-fg-dark px-3 py-2 text-sm font-semibold text-canvas dark:text-canvas-dark hover:opacity-90"
          >
            {addButton.label}
          </button>
        ) : null}
      </div>

      <div className="overflow-x-auto rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark">
        <table className="w-full text-sm">
          <thead className="bg-hairline/30 dark:bg-hairline-dark/40 text-fg dark:text-fg-dark">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className={`text-start px-3 py-2 font-semibold ${c.hideOnMobile ? "hidden md:table-cell" : ""}`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-6 text-center text-muted dark:text-muted-dark">
                  {t("common.loading")}
                </td>
              </tr>
            ) : empty ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-6 text-center text-muted dark:text-muted-dark">
                  {emptyMessage ?? t("common.noResults")}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className="border-t border-hairline dark:border-hairline-dark"
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-3 py-2 align-top ${c.hideOnMobile ? "hidden md:table-cell" : ""}`}
                    >
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {page !== undefined && totalPages !== undefined && totalPages > 1 && onPageChange ? (
        <div className="flex items-center justify-between gap-2 px-1">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-fg dark:text-fg-dark disabled:opacity-50 hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
          >
            <ChevronLeft className="h-4 w-4 rtl:hidden" aria-hidden />
            <ChevronRight className="h-4 w-4 ltr:hidden" aria-hidden />
            {t("common.previous")}
          </button>
          <span className="text-sm text-muted dark:text-muted-dark">
            {t("common.page")} {page} {t("common.of")} {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-fg dark:text-fg-dark disabled:opacity-50 hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
          >
            {t("common.next")}
            <ChevronRight className="h-4 w-4 rtl:hidden" aria-hidden />
            <ChevronLeft className="h-4 w-4 ltr:hidden" aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
}
